import { ACCOUNT_ROUTE, AUTH_ROUTE } from "constants/links"
import {
  CaretDown,
  CaretUpDown,
  User,
  Gear,
  SignOut,
  Users,
  Flask,
} from "@phosphor-icons/react"
import { Button } from "@tokenterminal/core/ui/button/Button"
import { IconButton } from "@tokenterminal/core/ui/button/IconButton"
import { LinkButton } from "@tokenterminal/core/ui/button/LinkButton"
import { Icon } from "@tokenterminal/core/ui/icon/Icon"
import { Divider } from "@tokenterminal/core/ui/layout/divider/Divider"
import { TierBadge } from "@tokenterminal/ui/TierBadge"
import { updateActiveOrganization } from "api/ApiCalls"
import { useAuth, type UserType } from "context/Auth"
import { usePersistedState } from "hooks/usePersistedState"
import Image from "next/image"
import { useRouter } from "next/router"
import { useFeatureFlagEnabled } from "posthog-js/react"
import { useCallback, forwardRef, useState, useRef, useMemo } from "react"
import { Dialog, Popover } from "react-aria-components"
import AccountDropdownSkeleton from "./AccountDropdownSkeleton"
import AccountSwitcherView from "./AccountSwitcherView"
import ActionItem from "./ActionItem"
import ExperimentalFeatureSwitcher from "./ExperimentalFeatureSwitcher"
import NumberFormatSwitcher, {
  DEFAULT_NUMBER_FORMAT,
  isNumberFormat,
  numberFormatOptions,
} from "./NumberFormatSwitcher"
import OrganizationDetails from "./OrganizationDetails"
import ThemeSwitcherToggle from "./ThemeSwitcherToggle"

function getSubscriptionLabelFromUser(user: UserType) {
  switch (true) {
    case user.isApi() && user.canManageSubscription:
      return "API"
    case user.isApi():
      return "API"
    case user.isPro() && user.subscriptionStatus.status !== "active":
      return "Trialing"
    case user.isPro():
      return "Pro"
    default:
      return "Free"
  }
}

const AccountSwitcherButton = ({
  handleOpenAccountSwitcher,
  activeOrganization,
}: {
  handleOpenAccountSwitcher: () => void
  activeOrganization: {
    name: string
    logo_url: string | null
  }
}) => {
  return (
    <Button
      onPress={handleOpenAccountSwitcher}
      className="w-full justify-between !px-2 !py-1.5 !h-auto"
    >
      <OrganizationDetails
        name={activeOrganization.name}
        logoUrl={activeOrganization.logo_url}
      />
      <Icon as={CaretUpDown} />
    </Button>
  )
}

const UserDetails = ({ user }: { user: UserType }) => {
  return (
    <div className="flex flex-col gap-0 max-w-full">
      <div className="text-sm font-medium">{user.display_name}</div>
      <div className="text-sm text-fg-secondary">{user.email}</div>
    </div>
  )
}

function SubscriptonLabel() {
  const auth = useAuth()

  if (!auth.isLoginFinished) {
    return (
      <TierBadge tier="pro" size="s" opacity={0}>
        Pro
      </TierBadge>
    )
  }

  return (
    <TierBadge tier={auth.user.subscriptionStatus.type} size="s" opacity={1}>
      {getSubscriptionLabelFromUser(auth.user)}
    </TierBadge>
  )
}

export const AccountDropdown = forwardRef(
  function AccountDropdown(props, forwardedRef) {
    const [isOpen, setIsOpen] = useState(false)
    const router = useRouter()
    const experimentalFeaturesEnabled = useFeatureFlagEnabled(
      "experimental_features"
    )
    const { logout, user, refetchUserData, isLoginFinished, isLoggedIn } =
      useAuth()

    const organizations = user?.organizations || []
    const activeOrganization = user?.activeOrganization
    const triggerRef = useRef<HTMLDivElement>(null)

    const [activeView, setActiveView] = useState<
      "initial" | "accountSwitcher" | "numberFormat" | "experimentalFeatures"
    >("initial")

    const [numberFormatRaw, setNumberFormat] = usePersistedState<
      string | undefined
    >("number-format", undefined, "localStorage")
    const numberFormatWithDefault = useMemo(
      () =>
        numberFormatRaw && isNumberFormat(numberFormatRaw)
          ? numberFormatRaw
          : DEFAULT_NUMBER_FORMAT,
      [numberFormatRaw]
    )

    const closeMenu = useCallback(() => {
      requestAnimationFrame(() => {
        setActiveView("initial")
      })
    }, [])

    const handleOpenAccountSwitcher = () => {
      requestAnimationFrame(() => {
        setActiveView("accountSwitcher")
      })
    }

    const handleBack = () => {
      requestAnimationFrame(() => {
        setActiveView("initial")
      })
    }

    const handleSetActiveOrganization = async (id: string) => {
      await updateActiveOrganization(id)
      refetchUserData()
      handleBack()
    }

    const handleClose = () => {
      setIsOpen(false)
      setActiveView("initial")
    }

    // Common dropdown items
    const commonDropdownItems = (
      <>
        <ActionItem
          item={{
            label: (
              <div className="flex items-center justify-between w-full">
                Formatting
                <div className="text-sm text-fg-secondary">
                  {
                    numberFormatOptions.find(
                      (option) => option.id === numberFormatWithDefault
                    )?.label
                  }
                </div>
              </div>
            ),
            onClick: () => setActiveView("numberFormat"),
          }}
        />
        {experimentalFeaturesEnabled && (
          <ActionItem
            item={{
              label: "Experimental features",
              onClick: () => setActiveView("experimentalFeatures"),
              icon: Flask,
            }}
          />
        )}
        <div className="flex gap-2 items-center justify-between pl-2.5 pr-1 py-1">
          <div className="text-sm">Theme</div>
          <ThemeSwitcherToggle />
        </div>
      </>
    )

    const views = {
      initial: (
        <div>
          <div>
            {isLoggedIn ? (
              <>
                <div className="flex flex-col p-3 gap-0">
                  <UserDetails user={user} />
                </div>
                <div className="flex flex-col gap-6 max-w-full px-2 mb-3">
                  {activeOrganization && (
                    <AccountSwitcherButton
                      handleOpenAccountSwitcher={handleOpenAccountSwitcher}
                      activeOrganization={activeOrganization}
                    />
                  )}
                </div>
                <Divider variant="secondary" />
                <div className="flex flex-col p-1 gap-0.5">
                  <ActionItem
                    item={{
                      label: "Manage account",
                      href: ACCOUNT_ROUTE,
                      icon: Gear,
                    }}
                    closeMenu={closeMenu}
                  />
                  {commonDropdownItems}
                </div>
                <Divider variant="secondary" />
                <div className="flex flex-col gap-1 p-1">
                  <ActionItem
                    item={{
                      label: "Sign out",
                      onClick: () => {
                        setIsOpen(false)
                        logout()
                        router.push(AUTH_ROUTE)
                      },
                      icon: SignOut,
                    }}
                    closeMenu={closeMenu}
                  />
                </div>
              </>
            ) : (
              <>
                <div className="flex flex-col p-1 g-1 lg:hidden">
                  <ActionItem
                    item={{
                      label: "Sign in",
                      href: "/auth/login",
                    }}
                  />
                  <ActionItem item={{ label: "Sign up", href: "auth" }} />
                </div>
                <Divider variant="secondary" className="block lg:hidden" />
                <div className="flex flex-col p-1 gap-0.5">
                  {commonDropdownItems}
                </div>
              </>
            )}
          </div>
        </div>
      ),
      accountSwitcher: (
        <AccountSwitcherView
          handleBack={handleBack}
          organizations={organizations}
          handleSetActiveOrganization={handleSetActiveOrganization}
          activeOrganizationId={activeOrganization?.id}
        />
      ),
      numberFormat: (
        <NumberFormatSwitcher
          handleBack={handleBack}
          numberFormat={numberFormatWithDefault}
          setNumberFormat={setNumberFormat}
        />
      ),
      experimentalFeatures: (
        <ExperimentalFeatureSwitcher handleBack={handleBack} />
      ),
    }

    // Return loading state
    if (!isLoginFinished) {
      return <AccountDropdownSkeleton />
    }

    // Return logged out state
    if (!isLoggedIn) {
      return (
        <div className="flex items-center gap-1 lg:gap-1.5">
          <div className="flex items-center gap-1 lg:gap-1.5">
            <LinkButton href="/auth/login" className="!px-3 hidden lg:flex">
              Sign in
            </LinkButton>
            <LinkButton
              href="/auth/signup"
              variant="ghost"
              className="!px-3 -mr-2.5 text-fg-secondary hidden lg:flex"
            >
              Sign up
            </LinkButton>
          </div>
          <div ref={triggerRef}>
            {/* Desktop */}
            <IconButton
              variant="ghost"
              className="!px-2 hidden lg:flex"
              onPress={() => setIsOpen(true)}
              icon={Gear}
              label="Settings"
            />
            {/* Mobile */}
            <Button
              variant="muted"
              className="tt-icon-button !px-2 !rounded-full flex lg:hidden"
              onPress={() => setIsOpen(true)}
            >
              <Icon as={User} width={16} height={16} />
            </Button>
          </div>
          <Popover
            className="tt-popover p-0 w-[280px]"
            isOpen={isOpen}
            onOpenChange={setIsOpen}
            triggerRef={triggerRef}
          >
            <Dialog className="focus:outline-none">{views[activeView]}</Dialog>
          </Popover>
        </div>
      )
    }

    // Return logged in state
    return (
      <div className="flex items-center gap-1 lg:gap-1.5">
        <div ref={triggerRef}>
          {/* Desktop */}
          <Button
            variant="ghost"
            className="!px-2 hidden lg:flex"
            onPress={() => setIsOpen(true)}
          >
            {user.activeOrganization.logo_url ? (
              <div className="flex gap-2 items-center justify-between">
                <Image
                  src={user.activeOrganization.logo_url!}
                  alt={user.activeOrganization.name}
                  width={22}
                  height={22}
                  style={{
                    width: "22px",
                    height: "22px",
                    borderRadius: "4px",
                    maxWidth: "unset",
                  }}
                />
              </div>
            ) : (
              <div className="flex items-center justify-center p-1 rounded mr-1 flex-shrink-0 bg-neutral-500/20 h-[24px] w-[24px]">
                <Icon as={Users} width={12} height={12} />
              </div>
            )}
            <div className="hidden lg:flex items-center gap-1">
              <span className="text-sm">{user.display_name}</span>{" "}
              <SubscriptonLabel />
            </div>
            <Icon as={CaretDown} width={14} className="flex-shrink-0" />
          </Button>

          {/* Mobile */}
          <Button
            variant="ghost"
            className="!px-2 flex lg:hidden"
            onPress={() => setIsOpen(true)}
          >
            {user.activeOrganization.logo_url ? (
              <div className="flex gap-2 items-center justify-between">
                <Image
                  src={user.activeOrganization.logo_url!}
                  alt={user.activeOrganization.name}
                  width={22}
                  height={22}
                  style={{
                    width: "22px",
                    height: "22px",
                    borderRadius: "4px",
                    maxWidth: "unset",
                  }}
                />
              </div>
            ) : (
              <div className="flex items-center justify-center p-1 rounded mr-1 flex-shrink-0 bg-neutral-500/20 h-[24px] w-[24px]">
                <Icon as={Users} width={12} height={12} />
              </div>
            )}
            <Icon as={CaretDown} width={14} className="flex-shrink-0" />
          </Button>
        </div>
        <Popover
          className="tt-popover p-0 w-[280px]"
          isOpen={isOpen}
          onOpenChange={handleClose}
          triggerRef={triggerRef}
        >
          <Dialog className="focus:outline-none">{views[activeView]}</Dialog>
        </Popover>
      </div>
    )
  }
)
